import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { useCompareSources, useSaveSalesAgentSettings } from '@/hooks/use-sales-agent-settings';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { PILL_BUTTON } from '../../ui-chrome';
import {
	emptySource,
	rowsProblem,
	sameRows,
	SOURCE_KINDS,
	toCompareRows,
	toRowDraft,
	type RowDraft,
	type SourceDraft,
	type SourceKind
} from '../compare-rows-draft';
import { COMPARE_LABEL_MAX_LENGTH, COMPARE_ROW_SOURCES_MAX, COMPARE_ROWS_MAX, type SalesAgentSettings } from '../types';

const INPUT = 'h-9 rounded-xl border-gray-500 py-0 text-xs leading-4 text-ink placeholder:text-gray-800';

/**
 * The chat's comparison table rows: what every comparison shows, in order. Each row reads its
 * value from the store's own product data (a tag, a Shopify option, the price or the
 * description), so nothing in the table is generated. Saved on its own, apart from the tab's
 * other settings.
 */
export const CompareRowsSettings = ({ settings }: { settings: SalesAgentSettings }) => {
	const saved = useMemo(() => settings.compareRows ?? [], [settings.compareRows]);
	const [rows, setRows] = useState<RowDraft[]>(() => saved.map((row) => toRowDraft(row)));
	const sources = useCompareSources();
	const save = useSaveSalesAgentSettings();

	useEffect(() => setRows(saved.map((row) => toRowDraft(row))), [saved]);

	const typeOptions = (sources.data?.productTypes ?? []).map((value) => ({
		label: value,
		value
	}));
	const tagOptions = (sources.data?.tags ?? []).map((value) => ({
		label: value,
		value
	}));
	const isDirty = !sameRows(toCompareRows(rows), saved);
	const problem = rowsProblem(rows);

	const updateRow = (id: string, patch: Partial<RowDraft>) =>
		setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
	const updateSource = (row: RowDraft, index: number, patch: Partial<SourceDraft>) =>
		updateRow(row.id, {
			sources: row.sources.map((source, i) => (i === index ? { ...source, ...patch } : source))
		});
	const move = (index: number, by: number) =>
		setRows((current) => {
			const next = [...current];
			const [row] = next.splice(index, 1);
			next.splice(index + by, 0, row!);
			return next;
		});

	const onSave = () => {
		if (problem) return;
		save.mutate(
			{ compareRows: toCompareRows(rows) },
			{
				onSuccess: () => toast.success('Compare rows saved'),
				onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the compare rows'))
			}
		);
	};

	return (
		<section className='flex w-full flex-col gap-3'>
			<div className='flex flex-col gap-1'>
				<h2 className='text-[13px] leading-[18px] font-medium text-gray-800'>Compare rows</h2>
				<p className='text-xs leading-4 text-gray-800'>
					The rows every product comparison shows, in this order. Each value is read from your Shopify data: the first
					source with a value is shown, “Not listed” when none has one, and “N/A” for a product type the row is not for.
					With no rows, the comparison shows each product&apos;s options.
				</p>
			</div>

			{rows.map((row, rowIndex) => (
				<div key={row.id} className='flex w-full flex-col gap-3 rounded-[20px] border border-gray-500 bg-white p-3'>
					<div className='flex items-center gap-2'>
						<Input
							aria-label={`Row ${rowIndex + 1} label`}
							placeholder='Label, e.g. Roast'
							value={row.label}
							maxLength={COMPARE_LABEL_MAX_LENGTH}
							onChange={(event) => updateRow(row.id, { label: event.target.value })}
							className={INPUT}
						/>
						<Button
							type='button'
							variant='ghost'
							size='icon'
							aria-label='Move row up'
							disabled={rowIndex === 0}
							onClick={() => move(rowIndex, -1)}
						>
							<ArrowUp className='size-3.5' />
						</Button>
						<Button
							type='button'
							variant='ghost'
							size='icon'
							aria-label='Move row down'
							disabled={rowIndex === rows.length - 1}
							onClick={() => move(rowIndex, 1)}
						>
							<ArrowDown className='size-3.5' />
						</Button>
						<Button
							type='button'
							variant='ghost'
							size='icon'
							aria-label='Remove row'
							onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}
						>
							<Trash2 className='size-3.5' />
						</Button>
					</div>

					<div className='flex flex-col gap-1.5'>
						<p className='text-xs leading-4 font-medium text-gray-900'>Applies to</p>
						<MultiSelect
							key={`${row.id}-types-${typeOptions.length}`}
							options={typeOptions}
							defaultValue={row.productTypes}
							onValueChange={(productTypes) => updateRow(row.id, { productTypes })}
							placeholder='Every product type'
							popoverPlaceholder='Search product types'
							showAllSelected
							hideSelectAll
						/>
					</div>

					<div className='flex flex-col gap-1.5'>
						<p className='text-xs leading-4 font-medium text-gray-900'>Value from (first one found)</p>
						{row.sources.map((source, index) => (
							<div key={index} className='flex items-start gap-2'>
								<Select
									value={source.kind}
									onValueChange={(kind) => updateSource(row, index, { kind: kind as SourceKind })}
								>
									<SelectTrigger aria-label='Source' className='h-9 w-40 shrink-0 rounded-xl text-xs'>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{SOURCE_KINDS.map((item) => (
											<SelectItem key={item.kind} value={item.kind}>
												{item.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<div className='min-w-0 flex-1'>
									{source.kind === 'option' && (
										<Input
											aria-label='Shopify option name'
											placeholder='Option name, e.g. Grind'
											value={source.name}
											onChange={(event) => updateSource(row, index, { name: event.target.value })}
											className={INPUT}
										/>
									)}
									{source.kind === 'tags' && (
										<MultiSelect
											key={`${row.id}-tags-${index}-${tagOptions.length}`}
											options={tagOptions}
											defaultValue={source.tags}
											onValueChange={(tags) => updateSource(row, index, { tags })}
											placeholder='Pick tags'
											popoverPlaceholder='Search tags'
											showAllSelected
											hideSelectAll
										/>
									)}
								</div>
								<Button
									type='button'
									variant='ghost'
									size='icon'
									aria-label='Remove source'
									disabled={row.sources.length === 1}
									onClick={() =>
										updateRow(row.id, {
											sources: row.sources.filter((_, i) => i !== index)
										})
									}
								>
									<Trash2 className='size-3.5' />
								</Button>
							</div>
						))}
						{row.sources.length < COMPARE_ROW_SOURCES_MAX && (
							<Button
								type='button'
								variant='ghost'
								className='self-start text-xs'
								onClick={() =>
									updateRow(row.id, {
										sources: [...row.sources, emptySource()]
									})
								}
							>
								<Plus className='size-3.5' /> Add a fallback source
							</Button>
						)}
					</div>
				</div>
			))}

			<div className='flex w-full items-center justify-between gap-3'>
				<Button
					type='button'
					variant='outline'
					className={cn(PILL_BUTTON, 'px-3')}
					disabled={rows.length >= COMPARE_ROWS_MAX}
					onClick={() => setRows((current) => [...current, toRowDraft()])}
				>
					<Plus className='size-3.5' /> Add row
				</Button>
				<div className='flex items-center gap-3'>
					{isDirty && problem && <p className='text-xs leading-4 text-red-600'>{problem}</p>}
					<Button
						type='button'
						disabled={!isDirty || Boolean(problem) || save.isPending}
						onClick={onSave}
						className={cn(PILL_BUTTON, 'px-3')}
					>
						{save.isPending && <Loader2 className='size-3.5 animate-spin' />}
						Save rows
					</Button>
				</div>
			</div>
		</section>
	);
};
